"use client"

import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import Image from 'next/image';
import AnimatedText from '../ui/AnimatedText';
import SectionTransition from '../ui/SectionTransition';
import { useTheme } from '../layout/ThemeProvider';
import { useParallax } from '../../hooks/useParallax';

interface HeroSectionProps {
  scrollY: number;
}

const HeroSection = ({ scrollY }: HeroSectionProps) => {
  const { theme } = useTheme();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [supportsWebp, setSupportsWebp] = useState(false);
  const [webpImageUrl, setWebpImageUrl] = useState<string>('');

  // Verificar suporte a WebP
  useEffect(() => {
    const checkWebpSupport = async () => {
      try {
        if (typeof window !== 'undefined') {
          console.log("User-Agent:", navigator.userAgent);
        }
        
        // Método mais robusto para verificar suporte a WebP
        const detectWebpSupport = () => {
          return new Promise<boolean>((resolve) => {
            const webpTest = document.createElement('img');
            // Base64 representação de uma imagem WebP de 1x1 pixel
            webpTest.src = 'data:image/webp;base64,UklGRiQAAABXRUJQVlA4IBgAAAAwAQCdASoBAAEAAwA0JaQAA3AA/vuUAAA=';
            webpTest.onload = () => resolve(true);
            webpTest.onerror = () => resolve(false);
          });
        };
        
        const supportsWebp = await detectWebpSupport();
        console.log("Navegador suporta WebP:", supportsWebp, "- Plataforma:", navigator?.platform || "desconhecida");
        setSupportsWebp(supportsWebp);
      } catch {
        console.log("Erro ao verificar suporte WebP, usando fallback");
        setSupportsWebp(false);
      }
    };
    
    checkWebpSupport();
  }, []);

  // Configurar URL completa para a imagem
  useEffect(() => {
    if (typeof window !== 'undefined' && supportsWebp) {
      const baseUrl = window.location.origin;
      setWebpImageUrl(`${baseUrl}/images/tech-circuit-bg.webp`);
    }
  }, [supportsWebp]);

  // Preload da imagem de background
  useEffect(() => {
    const preloadBackgroundImage = async () => {
      try {
        if (typeof window !== 'undefined') {
          // Usar o caminho absoluto para facilitar o debugging
          const baseUrl = window.location.origin;
          const imgSrc = supportsWebp ? `${baseUrl}/images/tech-circuit-bg.webp` : '';
          
          console.log("URL base:", baseUrl);
          console.log("Caminho completo da imagem:", imgSrc);
          
          if (imgSrc) {
            // Verificar se a imagem existe com um fetch
            try {
              const response = await fetch(imgSrc, { method: 'HEAD' });
              if (!response.ok) {
                console.error(`Imagem não encontrada: ${imgSrc}, status: ${response.status}`);
                setImageLoaded(true);
                return;
              }
            } catch (fetchError) {
              console.error("Erro ao verificar existência da imagem:", fetchError);
            }
            
            const img = document.createElement('img');
            console.log("Carregando imagem WebP:", imgSrc);
            img.src = imgSrc;
            img.onload = () => {
              console.log("Imagem WebP carregada com sucesso");
              setImageLoaded(true);
            };
            img.onerror = (e) => {
              console.log("Erro ao carregar imagem WebP:", e);
              setImageLoaded(true);
            };
          } else {
            console.log("Usando fallback sem imagem");
            setImageLoaded(true);
          }
        }
      } catch (error) {
        console.error('Erro ao carregar imagem de background:', error);
        setImageLoaded(true); 
      }
    };
    
    preloadBackgroundImage();
  }, [supportsWebp]);

  // Usamos o hook de parallax externo
  const parallax = useParallax(scrollY);

  // Animation variants
  const slideUp = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.6 } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  return (
    <SectionTransition id="hero">
      {/* Wrapper com overflow hidden para confinar o parallax */}
      <div className="relative overflow-hidden">
        {/* Seção principal */}
        <section 
          className={`relative w-full flex items-center ${
            theme === 'dark'
              ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900'
              : 'bg-gradient-to-br from-blue-900 via-indigo-900 to-blue-950'
          }`}
          aria-label="Seção principal"
        >
          {/* Background image tech-circuit */}
          <div 
            className="absolute inset-0 z-0 bg-cover bg-center mix-blend-overlay" 
            style={{
              backgroundImage: imageLoaded && supportsWebp && webpImageUrl
                ? `url('${webpImageUrl}')` 
                : 'none',
              backgroundColor: theme === 'dark' ? '#1a202c' : '#f0f4f8',
              transform: scrollY > 800 ? 'none' : `translateY(${scrollY * 0.2}px)`,
              opacity: scrollY > 800 ? 0 : 0.6,
              transition: 'opacity 0.3s ease-out',
            }}
            aria-hidden="true"
          ></div>

          {/* Decorative background elements com posicionamento relativo */}
          <div className="absolute inset-0 z-1 opacity-10">
            <div className="absolute inset-0 bg-[url('/images/dot-pattern.png')] bg-repeat opacity-10"></div>
          </div>
          
          {/* Animated decorative circles com dimensões responsivas */}
          <motion.div 
            style={{ y: parallax.fast }} // Uso do hook de parallax
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.2, 0.3, 0.2],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className={`absolute top-1/4 -left-1/4 w-[50%] h-[50%] ${
              theme === 'dark' ? 'bg-blue-800' : 'bg-blue-600'
            } rounded-full mix-blend-overlay filter blur-[64px] opacity-20 z-10`}
            aria-hidden="true"
          ></motion.div>
          
          <motion.div 
            style={{ y: parallax.reverse }} // Uso do hook de parallax
            animate={{
              scale: [1, 1.3, 1],
              opacity: [0.2, 0.25, 0.2],
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 1
            }}
            className={`absolute bottom-1/4 -right-1/4 w-[60%] h-[60%] ${
              theme === 'dark' ? 'bg-indigo-800' : 'bg-indigo-400'
            } rounded-full mix-blend-overlay filter blur-[80px] opacity-20 z-10`}
            aria-hidden="true"
          ></motion.div>
          
          {/* Container principal com padding controlado */}
          <div className="container mx-auto px-4 py-20 md:py-28 w-full relative z-20">
            <div className="flex flex-col lg:flex-row items-center">
              <motion.div 
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="lg:w-1/2 lg:pr-12 mb-12 lg:mb-0"
              >
                {/* Badge com posicionamento relativo - ADICIONADO mt-4 para espaçamento do logo */}
                <motion.div 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.6 }}
                  className="inline-flex items-center px-3 py-1 space-x-2 text-xs font-medium tracking-wider uppercase bg-blue-600/20 backdrop-blur-md text-blue-100 rounded-full mb-3 mt-12 border border-white/10"
                >
                  <span className="inline-block w-2 h-2 rounded-full bg-blue-400 animate-pulse"></span>
                  <span>PARCEIROS EM TECNOLOGIA</span>
                </motion.div>
                
                {/* Title with typing animation - melhorado com tags h1 para SEO */}
                <header className="mt-2">
                  <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
                    <span className="sr-only">Web Imports - </span>
                    <AnimatedText 
                      text="Soluções sob medida" 
                      className="block mb-2"
                      delay={0.3}
                    />
                    <AnimatedText 
                      text="para o seu sucesso" 
                      className="block"
                      delay={0.5}
                    />
                  </h1>
                </header>
                
                <motion.p 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4, duration: 0.6 }}
                  className="text-lg md:text-xl text-blue-100 mt-6 mb-8 font-light leading-relaxed max-w-lg"
                >
                  Entendemos suas necessidades específicas e desenvolvemos estratégias
                  tecnológicas personalizadas que impulsionam sua produtividade.
                </motion.p>
                
                <motion.div 
                  variants={staggerContainer}
                  initial="hidden"
                  animate="visible"
                  className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 mb-6"
                >
                  <motion.a 
                    variants={slideUp}
                    whileHover={{ scale: 1.05, boxShadow: "0 10px 25px -5px rgba(59, 130, 246, 0.5)" }}
                    whileTap={{ scale: 0.95 }}
                    href="#servicos" 
                    className="group relative overflow-hidden px-8 py-4 rounded-xl text-white bg-gradient-to-r from-blue-600 to-blue-800 text-center shadow-lg hover:shadow-xl transition-all"
                    aria-label="Explorar serviços"
                  >
                    <span className="relative z-10 font-medium">Explorar Serviços</span>
                    <span className="absolute inset-0 bg-gradient-to-r from-blue-700 to-indigo-800 opacity-0 group-hover:opacity-100 transition-opacity"></span>
                  </motion.a>
                </motion.div>
              </motion.div>
              
              <motion.div 
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
                style={{ y: parallax.reverseFast }} // Uso do hook de parallax
                className="lg:w-1/2 relative"
              >
                {/* Decorations for main image - posicionamento responsivo */}
                <div className="absolute -top-5 -left-5 w-1/4 h-1/4 bg-blue-600/20 rounded-full filter blur-3xl"></div>
                <div className="absolute -bottom-5 -right-5 w-1/4 h-1/4 bg-indigo-600/20 rounded-full filter blur-3xl"></div>
                
                {/* Main frame */}
                <motion.div 
                  initial={{ rotate: 5, scale: 0.95 }}
                  animate={{ rotate: 1, scale: 1 }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                  className="relative z-10"
                >
                  <motion.div 
                    whileHover={{ rotate: 0, scale: 1.02 }}
                    transition={{ duration: 0.3 }}
                    className={`relative p-2 ${
                      theme === 'dark'
                        ? 'bg-gradient-to-r from-gray-800/30 via-gray-700/40 to-gray-800/30'
                        : 'bg-gradient-to-r from-blue-200/30 via-white/40 to-indigo-200/30'
                    } backdrop-blur-xl rounded-2xl rotate-1 shadow-xl`}
                  >
                    <div className={`aspect-video relative overflow-hidden rounded-xl ${
                      theme === 'dark' ? 'bg-gray-900' : 'bg-blue-950'
                    }`}>
                      <div className="absolute inset-0 w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-800 to-blue-950">
                        <Image 
                          src="/images/microsolda2.jpg"
                          alt="Equipamento de microsolda especializada utilizado pela Web Imports para reparo de placas eletrônicas de alta precisão" 
                          width={1200}
                          height={675}
                          priority={true}
                          fetchPriority="high"
                          loading="eager"
                          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 800px"
                          quality={85}
                          placeholder="blur"
                          blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAMCAgICAgMCAgIDAwMDBAYEBAQEBAgGBgUGCQgKCgkICQkKDA8MCgsOCwkJDRENDg8QEBEQCgwSExIQEw8QEBD/wAALCAAIAAoBAREA/8QAFQABAQAAAAAAAAAAAAAAAAAAAAf/xAAUEAEAAAAAAAAAAAAAAAAAAAAA/9oACAEBAAA/AH+P/9k="
                          className="w-full h-full object-cover rounded-lg"
                        />
                      </div>
                      
                      {/* Info overlay - adicionado h2 para SEO */}
                      <div className={`absolute bottom-0 left-0 right-0 p-8 ${
                        theme === 'dark'
                          ? 'bg-gradient-to-t from-gray-950 to-transparent'
                          : 'bg-gradient-to-t from-blue-950 to-transparent'
                      }`}>
                        <div className="mb-3">
                          <h2 className="text-white text-2xl font-light">Tecnologia avançada</h2>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </motion.div>
              </motion.div>
            </div>
          </div>
          
          {/* Wave divider melhorado - ajustado para eliminar espaços extras */}
          <div className="absolute left-0 right-0 bottom-0 w-full overflow-hidden z-30">
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              viewBox="0 0 1440 200" 
              className={`w-full ${theme === 'dark' ? 'text-gray-900' : 'text-gray-50'}`}
              preserveAspectRatio="none"
              width="100%"
              height="150"
              aria-hidden="true"
            >
              <path 
                fill="currentColor" 
                fillOpacity="1" 
                d="M0,160L48,144C96,128,192,96,288,85.3C384,75,480,85,576,101.3C672,117,768,139,864,133.3C960,128,1056,96,1152,80C1248,64,1344,64,1392,64L1440,64L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
              ></path>
            </svg>
          </div>
        </section>
      </div>
    </SectionTransition>
  );
};

export default HeroSection;